/**
 * 统一 API 请求封装
 * 处理 token 失效自动跳转登录
 */

import { message } from 'antd';
import { API_BASE_URL } from './config';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * 封装的 fetch 请求
 * - 自动添加 Authorization header
 * - 处理 401 错误（token 失效/被踢出）
 */
export async function request<T = any>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { skipAuth = false, headers = {}, ...restOptions } = options;

  const token = localStorage.getItem('token');

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers as Record<string, string>,
  };

  if (!skipAuth && token) {
    finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  const hasBasePath = API_BASE_URL.startsWith('/') && endpoint.startsWith(API_BASE_URL);
  const url = endpoint.startsWith('http') || hasBasePath ? endpoint : `${API_BASE_URL}${endpoint}`;

  const response = await fetch(url, {
    ...restOptions,
    headers: finalHeaders,
  });

  // 处理 401 错误 - token 失效或被踢出
  if (response.status === 401) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail || '登录已过期';
    
    // 清除本地存储
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    
    // 显示提示
    message.error(detail);
    
    // 跳转登录页
    setTimeout(() => {
      window.location.href = '/login';
    }, 1000);
    
    throw new Error(detail);
  }

  // 处理其他错误
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || `请求失败: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * GET 请求
 */
export function get<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: 'GET' });
}

/**
 * POST 请求
 */
export function post<T = any>(
  endpoint: string,
  body?: any,
  options?: RequestOptions
): Promise<T> {
  return request<T>(endpoint, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * PUT 请求
 */
export function put<T = any>(
  endpoint: string,
  body?: any,
  options?: RequestOptions
): Promise<T> {
  return request<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * DELETE 请求
 */
export function del<T = any>(endpoint: string, options?: RequestOptions): Promise<T> {
  return request<T>(endpoint, { ...options, method: 'DELETE' });
}
